import { ArrowRight, Lock, Search, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { PageHeader } from "@/components/AppShell";
import { Badge, Card, cx, EmptyState, Input } from "@/components/ui";
import { CHANGE_KIND_LABEL, CHANGELOG, markAllSeen, readSeen, type ChangeKind } from "@/lib/changelog";
import { intlLocale, t } from "@/lib/i18n";

const KIND_TONE: Record<ChangeKind, "success" | "info" | "neutral"> = { new: "success", improved: "info", fixed: "neutral" };
const dayLabel = (ymd: string) => new Date(`${ymd}T12:00:00Z`).toLocaleDateString(intlLocale(), { weekday: "long", day: "numeric", month: "long", year: "numeric" });

/** Logiful intern: Neuerungen zum Nachlesen fürs Team */
export function AdminChangelogPage() {
  // beim Öffnen gelesene Einträge merken; „neu“ bleibt für diesen Besuch sichtbar
  const [unseen] = useState(() => { const s = readSeen(); return new Set(CHANGELOG.filter((e) => !s.has(e.id)).map((e) => e.id)); });
  useEffect(() => markAllSeen(), []);
  const [kind, setKind] = useState<ChangeKind | "all">("all");
  const [onlyInternal, setOnlyInternal] = useState(false);
  const [search, setSearch] = useState("");
  const s = search.trim().toLowerCase();
  const list = useMemo(
    () => CHANGELOG.filter((e) => (kind === "all" || e.kind === kind) && (!onlyInternal || !e.customerVisible || !!e.internal) && (!s || [e.title, e.summary, e.area, e.internal ?? "", ...(e.details ?? [])].join(" ").toLowerCase().includes(s))),
    [kind, onlyInternal, s],
  );
  const days = [...new Set(list.map((e) => e.date))];

  return (
    <div className="pb-16">
      <PageHeader title={t("Neuerungen")} description={t("Was sich in Connect geändert hat – mit internen Hinweisen fürs Logiful-Team. Neueste zuerst.")} />
      <div className="px-4 md:px-8 space-y-4 max-w-4xl">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex p-0.5 rounded-lg bg-surface-2 border border-border">
            {(["all", "new", "improved", "fixed"] as const).map((k) => (
              <button key={k} onClick={() => setKind(k)} aria-pressed={kind === k} className={cx("h-7 px-3 rounded-md text-[12.5px] font-medium", kind === k ? "bg-surface shadow-sm" : "text-fg-muted hover:text-fg")}>
                {k === "all" ? t("Alle") : t(CHANGE_KIND_LABEL[k])}
              </button>
            ))}
          </div>
          <label className="inline-flex items-center gap-2 text-[13px]"><input type="checkbox" checked={onlyInternal} onChange={(e) => setOnlyInternal(e.target.checked)} />{t("Nur mit internen Hinweisen")}</label>
          <div className="ml-auto w-full sm:w-64"><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("Suchen …")} leading={<Search className="size-3.5" />} /></div>
        </div>

        {!list.length ? (
          <EmptyState icon={<Sparkles className="size-5" />} title={t("Nichts gefunden")}>{t("Andere Suche oder Filter probieren.")}</EmptyState>
        ) : (
          days.map((d) => (
            <section key={d} className="space-y-2">
              <h2 className="text-[12.5px] font-semibold text-fg-muted uppercase tracking-wide pt-2">{dayLabel(d)}</h2>
              {list.filter((e) => e.date === d).map((e) => (
                <Card key={e.id} className={cx("p-4", unseen.has(e.id) && "ring-1 ring-ink/20")}>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge tone={KIND_TONE[e.kind]}>{t(CHANGE_KIND_LABEL[e.kind])}</Badge>
                    <Badge>{e.area}</Badge>
                    {!e.customerVisible && <Badge icon={<Lock className="size-3" />}>{t("nur intern")}</Badge>}
                    {unseen.has(e.id) && <span className="text-[11.5px] font-semibold text-success">{t("ungelesen")}</span>}
                  </div>
                  <h3 className="mt-2 font-semibold text-[14.5px]">{e.title}</h3>
                  <p className="mt-1 text-[13px] text-fg-muted leading-relaxed">{e.summary}</p>
                  {e.details?.length ? (
                    <ul className="mt-2 space-y-0.5 text-[13px] list-disc pl-5 marker:text-fg-subtle">
                      {e.details.map((x) => <li key={x}>{x}</li>)}
                    </ul>
                  ) : null}
                  {e.internal && (
                    <div className="mt-3 rounded-lg border border-warning/30 bg-warning-soft/40 px-3 py-2 text-[12.5px] flex gap-2">
                      <Lock className="size-3.5 mt-0.5 shrink-0 text-warning" />
                      <span><span className="font-semibold">{t("Intern:")}</span> {e.internal}</span>
                    </div>
                  )}
                  {e.to && <Link to={e.to} className="mt-3 inline-flex items-center gap-1 text-[12.5px] font-medium text-fg-muted hover:text-fg">{t("Ansehen")} <ArrowRight className="size-3.5" /></Link>}
                </Card>
              ))}
            </section>
          ))
        )}
      </div>
    </div>
  );
}
